/**
 * ProductRecJun26
 * ───────────────────────────────────────────────────────────
 * Jun 2026 variant of the product recommendations section.
 *
 * Responsibilities:
 *   1. Fetch Shopify recommendation results via the section
 *      rendering API (same approach as the original section).
 *   2. Inject the returned HTML into the custom element.
 *   3. Initialise a CSS scroll-snap carousel with dot indicators
 *      for mobile. On desktop the Foundation grid does the work
 *      and carousel logic is skipped.
 *
 * IMPORTANT: This class uses a unique custom element name
 * (product-rec-jun26) and touches no shared code. The existing
 * product-recommendations element is completely unaffected.
 */

class ProductRecJun26 extends HTMLElement {
  connectedCallback() {
    if (this.dataset.url) {
      this.fetchProducts();
    } else {
      // Products are server-rendered (metafield approach) — skip fetch, init carousel directly
      this.classList.add('product-rec-jun26--loaded');
      this.initCarousel();
      document.dispatchEvent(new Event('recommendations-loaded'));
    }
  }

  fetchProducts() {
    fetch(this.dataset.url)
      .then(response => response.text())
      .then(text => {
        const doc    = new DOMParser().parseFromString(text, 'text/html');
        const source = doc.querySelector('product-rec-jun26');

        if (source && source.innerHTML.trim().length) {
          const cards = source.querySelectorAll('product-card, [data-product-id]');
          if (cards.length > 0) {
            this.innerHTML += source.innerHTML;
            this.initCarousel();
          }
        }

        this.classList.add('product-rec-jun26--loaded');
        document.dispatchEvent(new Event('recommendations-loaded'));
      })
      .catch(err => console.error('[product-rec-jun26]', err));
  }

  initCarousel() {
    const track         = this.querySelector('.prjun__track');
    const dotsContainer = this.querySelector('.prjun__dots');
    const slides        = Array.from(this.querySelectorAll('.prjun__slide'));

    if (!track || !dotsContainer || slides.length === 0) return;

    // ── Build one dot per slide ─────────────────────────────
    slides.forEach((slide, i) => {
      const dot  = document.createElement('button');
      dot.type   = 'button';
      dot.className = 'prjun__dot' + (i === 0 ? ' prjun__dot--active' : '');
      dot.setAttribute('aria-label', 'Go to product ' + (i + 1));

      dot.addEventListener('click', () => {
        // scrollIntoView is the most reliable way to honour scroll-snap
        slide.scrollIntoView({
          behavior: 'smooth',
          block:    'nearest',
          inline:   'start'
        });
      });

      dotsContainer.appendChild(dot);
    });

    const dots = Array.from(dotsContainer.querySelectorAll('.prjun__dot'));

    // ── Update active dot after scroll settles (debounced) ──
    let scrollTimer;
    track.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        const trackLeft = track.getBoundingClientRect().left;
        let activeIndex = 0;
        let minDist     = Infinity;

        slides.forEach((slide, i) => {
          const dist = Math.abs(slide.getBoundingClientRect().left - trackLeft);
          if (dist < minDist) {
            minDist     = dist;
            activeIndex = i;
          }
        });

        dots.forEach((dot, i) => {
          dot.classList.toggle('prjun__dot--active', i === activeIndex);
        });
      }, 60);
    }, { passive: true });
  }
}

customElements.define('product-rec-jun26', ProductRecJun26);
